// Общая логика ввода и разбора диапазона страниц («1-5», «2,4,7»).
// Используется экранной клавиатурой (NumberKeyboard), модалкой диапазона (RangeModal)
// и превью документа (PdfView/DocView), чтобы правила были в одном месте.

const PAGE_REGEX = /^\d+(-\d+)?(,\d+(-\d+)?)*$/;
const MAX_INPUT_LENGTH = 20;
const MAX_PAGE_NO_LIMIT = 9999; // если maxPage не передан, защита от мусора

// Ошибки возвращаются кодом + параметрами интерполяции, а не готовой строкой:
// переводы лежат в editDoc.range.errors.*
export function validatePageInput(raw, maxPage) {
    let cleaned = (raw || "").trim();

    if (!cleaned) {
        return { valid: false, code: "empty" };
    }

    // человек дописал разделитель и не закончил число (5-, 5,) — просто отрезаем хвост
    const withoutTrailingSeparator = cleaned.replace(/[-,]+$/, "");
    if (!withoutTrailingSeparator) {
        return { valid: false, code: "incomplete" };
    }
    cleaned = withoutTrailingSeparator;

    if (!PAGE_REGEX.test(cleaned)) {
        return { valid: false, code: "format" };
    }

    const parts = cleaned.split(",");
    for (const part of parts) {
        const [startStr, endStr] = part.split("-");
        const start = parseInt(startStr, 10);
        const end = endStr !== undefined ? parseInt(endStr, 10) : start;

        if (start === 0 || end === 0) {
            return { valid: false, code: "zero" };
        }
        if (start > end) {
            return { valid: false, code: "range", params: { part } };
        }
        if (maxPage) {
            if (start > maxPage || end > maxPage) {
                return { valid: false, code: "max", params: { maxPage } };
            }
        } else if (start > MAX_PAGE_NO_LIMIT || end > MAX_PAGE_NO_LIMIT) {
            return { valid: false, code: "too_large" };
        }
    }

    return { valid: true, cleaned };
}

// Правила посимвольного ввода. Возвращает либо новое значение, либо код ошибки.
export function appendPageChar(value, char) {
    const lastChar = value.slice(-1);

    if (value.length >= MAX_INPUT_LENGTH) {
        return { code: "too_long" };
    }
    if (char === "0" && (value === "" || lastChar === "-" || lastChar === ",")) {
        return { code: "zero" };
    }
    if (char === "-" || char === ",") {
        if (value === "") {
            return { code: "separator_first" };
        }
        if (lastChar === "-" || lastChar === ",") {
            return { code: "separator_dup" };
        }
    }

    return { value: value + char };
}

export function pageErrorText(t, code, params) {
    return t(`editDoc.range.errors.${code}`, params);
}

// «1-3,7» + maxPage=10 → [1,2,3,7]. Невалидные/выходящие за пределы номера отбрасываются.
export function parsePageList(customPages, maxPage) {
    if (!customPages) return [];
    return customPages
        .split(",")
        .flatMap((range) => {
            const trimmedRange = range.trim();
            const [start, end] = trimmedRange.includes("-")
                ? trimmedRange.split("-").map(Number)
                : [Number(trimmedRange)];
            return end
                ? Array.from({ length: end - start + 1 }, (_, i) => start + i)
                : [Number(trimmedRange)];
        })
        .filter((page) => page >= 1 && (!maxPage || page <= maxPage));
}

// Сколько страниц реально уйдёт в печать при выбранном режиме.
export function countPagesToPrint(printType, customPages, totalPages) {
    if (printType === "first_page" || printType === "last_page") return 1;
    if (printType === "custom") return parsePageList(customPages, totalPages).length;
    return totalPages;
}

// Какие страницы уйдут в печать (без повторов, по возрастанию) — для
// сверки с пустыми страницами документа.
export function selectedPageList(printType, customPages, totalPages) {
    if (printType === "first_page") return [1];
    if (printType === "last_page") return [totalPages];
    if (printType === "custom") {
        return [...new Set(parsePageList(customPages, totalPages))].sort((a, b) => a - b);
    }
    return Array.from({ length: totalPages }, (_, i) => i + 1);
}

// [1,2,3,5,7,8] → «1-3,5,7-8»: обратное к parsePageList, в формате RangeModal.
export function formatPageList(pages) {
    const sorted = [...new Set(pages)].sort((a, b) => a - b);
    const parts = [];
    for (let i = 0; i < sorted.length; i++) {
        const start = sorted[i];
        while (sorted[i + 1] === sorted[i] + 1) i++;
        parts.push(start === sorted[i] ? String(start) : `${start}-${sorted[i]}`);
    }
    return parts.join(",");
}
